/** New Windex filter = one entry. control: select | switch. */
export const WINDEX_CONTACT_FILTERS = [
  {
    key: 'year',
    label: 'Academic year',
    control: 'select',
    param: 'year',
    allLabel: 'All years',
  },
  {
    key: 'class',
    label: 'Class',
    control: 'select',
    param: 'class',
    allLabel: 'All classes',
    needs: 'year',
    disabledLabel: 'Select an academic year first',
  },
  {
    key: 'pendingFees',
    label: 'Pending fees',
    control: 'switch',
    endpoint: 'pending-fees',
    needs: 'year',
  },
];

/** Academic month index the pending-fees API expects. 1 is June. */
export const WINDEX_FEE_MONTHS = [
  ['1', '1 - June'],
  ['2', '2 - July'],
  ['3', '3 - August'],
  ['4', '4 - September'],
  ['5', '5 - October'],
  ['6', '6 - November'],
  ['7', '7 - December'],
  ['8', '8 - January'],
  ['9', '9 - February'],
  ['10', '10 - March'],
  ['11', '11 - April'],
  ['12', '12 - May'],
];

export function emptyWindexFilters() {
  return {
    ...Object.fromEntries(
      WINDEX_CONTACT_FILTERS.map((item) => [
        item.key,
        item.control === 'switch' ? false : '',
      ]),
    ),
    month: '1',
  };
}

export function windexQueryParams(filters) {
  const params = {};
  for (const item of WINDEX_CONTACT_FILTERS) {
    if (!item.param || !filters[item.key]) continue;
    if (item.needs && !filters[item.needs]) continue;
    params[item.param] = filters[item.key];
  }
  return params;
}

const WINDEX_FILTER_ORDER = ['year', 'class', 'pendingFees'];

const WINDEX_FILTER_LABELS = {
  year: 'Academic Year',
  class: 'Class',
  pendingFees: 'Pending Fees',
};

/** Later filters need every earlier one already added. */
export function windexFilterBlocked(key, active) {
  const index = WINDEX_FILTER_ORDER.indexOf(key);
  if (index <= 0) return '';
  const missing = WINDEX_FILTER_ORDER.slice(0, index).filter(
    (item) => !active.includes(item),
  );
  if (!missing.length) return '';
  return `Add ${missing.map((item) => WINDEX_FILTER_LABELS[item]).join(' and ')} first`;
}

/** Removing a filter also drops the ones that depend on it. */
export function windexFiltersToDrop(key) {
  const index = WINDEX_FILTER_ORDER.indexOf(key);
  if (index < 0) return [key];
  return WINDEX_FILTER_ORDER.slice(index);
}

export function pendingFeesActive(filters) {
  return WINDEX_CONTACT_FILTERS.some(
    (item) => item.endpoint === 'pending-fees' && filters[item.key],
  );
}

export function windexFilterMessage(error) {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  if (status === 400 && data.code === 'windex_not_activated') {
    return 'Windex is disconnected.';
  }
  if (status === 400 && data.year != null) {
    return 'Choose an academic year before filtering by class.';
  }
  if (status === 401) return 'Session expired.';
  return '';
}

export function naturalSortAscending(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function normalizeSyncedYears(data) {
  const raw = Array.isArray(data) ? data : data?.years || data?.results || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (typeof row === 'string' || typeof row === 'number') {
        return { year: String(row), classes: [] };
      }
      if (!row || typeof row !== 'object') return null;
      const year = row.year ?? row.academic_year ?? row.name ?? '';
      const classRaw = row.classes || row.class_names || [];
      const classes = (Array.isArray(classRaw) ? classRaw : [])
        .map((item) =>
          typeof item === 'string' || typeof item === 'number'
            ? String(item)
            : item?.name || item?.class || item?.label || '',
        )
        .filter(Boolean)
        .sort(naturalSortAscending);
      if (!year) return null;
      return { year: String(year), classes };
    })
    .filter(Boolean)
    .sort((a, b) => naturalSortAscending(a.year, b.year));
}

export function normalizeContactList(data) {
  const list = Array.isArray(data)
    ? data
    : data?.results || data?.contacts || [];
  return {
    results: list.map((row, index) => ({
      ...row,
      id: row.id ?? row.contact_number ?? row.mobile_no ?? `row-${index}`,
      phone: row.phone || row.mobile_no || row.contact_number || '',
      pending_fee: row.pending_fee ?? row.pending_fees ?? row.fee_amount,
      student_class:
        row.student_class || row.class_name || row.class || row.std || '',
      academic_year: row.academic_year || row.year || '',
    })),
    count: data?.count ?? list.length,
  };
}
